import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  inject,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import {
  ParticipantMedia,
  LiveKitRoomService,
} from '../../../../core/services/livekit/livekit-room.service';
import { GroupCallHubService } from '../../../../core/services/group-call-hub/group-call-hub.service';
import { GroupCallOverlayService } from '../../../../core/services/call-overlay/group-call-overlay.service';
import {
  CallType,
  ParticipantRole,
} from '../../../../core/models/group-call/group-call.model';
import { ParticipantTileComponent } from '../participant-tile/participant-tile.component';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { AudioUnlockService } from '../../../../core/services/audio/audio-unlock.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { DoctorNamePipe } from '../../../../core/pipes/doctor-name.pipe';
import { MediaPermissionStore } from '../../../../core/services/consent/media-permission.store';

@Component({
  selector: 'app-group-call',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ParticipantTileComponent,
    DoctorNamePipe,
  ],
  templateUrl: './group-call.component.html',
  styleUrls: ['./group-call.component.scss'],
})
export class GroupCallComponent implements OnInit, OnDestroy {
  @Input() callId = '';
  @Input() roomName = '';
  @Input() token = '';
  @Input() serverUrl = '';
  @Input() title = '';
  @Input() callType = CallType.Video;
  @Input() skipPermissionCheck = false;

  @Output() callEnded = new EventEmitter<void>();

  @ViewChild('minVideo', { static: false })
  minVideo?: ElementRef<HTMLVideoElement>;

  private readonly destroy$ = new Subject<void>();
  private liveKitRoom = inject(LiveKitRoomService);
  private groupCallHub = inject(GroupCallHubService);
  private overlayService = inject(GroupCallOverlayService);
  private cdr = inject(ChangeDetectorRef);
  private currentUserService = inject(CurrentUserService);
  private audioUnlock = inject(AudioUnlockService);
  private translate = inject(TranslateService);
  private toastService = inject(ToastService);
  private mediaPermissionStore = inject(MediaPermissionStore);

  localParticipant: ParticipantMedia | null = null;
  remoteParticipants: ParticipantMedia[] = [];

  isMuted = false;
  isVideoOff = false;
  isMinimized = false;
  mediaPermissionDenied = false;
  canSendAudio = true;
  canSendVideo = true;
  private lastPartialToastKey?: string;
  private initialMediaCheckDone = false;

  duration = '00:00';
  private startTime = Date.now();
  private timerInterval: any;

  private dragActive = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private resizeActive = false;
  private resizeEdge = '';
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private resizeStartLeft = 0;
  private resizeStartBottom = 0;

  minimizedLeft = 20;
  minimizedBottom = 20;
  minimizedWidth = 220;
  minimizedHeight = 140;

  private myRole: ParticipantRole | null = null;
  private endTonePlayed = false;
  private readonly participantMetaByUserId = new Map<
    string,
    {
      name?: string;
      branchName?: string;
      professionalTitle?: number | string | null;
    }
  >();

  ngOnInit(): void {
    this.initRoom();
    this.startTimer();
    this.subscribeToHubEvents();
    this.subscribeToOverlayState();
    document.addEventListener('keydown', this.onKeyDown);
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.destroy$.next();
    this.destroy$.complete();
    this.liveKitRoom.disconnectFromRoom();
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('mouseup', this.onDocMouseUp);
  }

  private async initRoom(): Promise<void> {
    try {
      await this.mediaPermissionStore.syncFromServer();
      const isVideoCall = this.callType === CallType.Video;
      const callerPerms = this.mediaPermissionStore.permissions();
      this.canSendAudio = !!callerPerms.audioAllowed;
      this.canSendVideo = !!callerPerms.videoAllowed && isVideoCall;
      this.showPartialPermissionToast(
        this.canSendAudio,
        this.canSendVideo,
        isVideoCall
      );

      await this.liveKitRoom.connectToRoom(this.serverUrl, this.token, {
        video: this.canSendVideo,
        audio: this.canSendAudio,
      });

      await this.liveKitRoom.startAudio();

      this.liveKitRoom
        .getLocalParticipant$()
        .pipe(takeUntil(this.destroy$))
        .subscribe((lp) => {
          const resolved = this.withMeta(lp);
          if (!this.initialMediaCheckDone && resolved) {
            if (this.canSendAudio && !resolved.isMicEnabled) {
              this.canSendAudio = false;
            }
            if (
              this.callType === CallType.Video &&
              this.canSendVideo &&
              !resolved.isCameraEnabled
            ) {
              this.canSendVideo = false;
            }
            this.initialMediaCheckDone = true;
          }
          this.localParticipant = resolved;
          this.isMuted = !resolved?.isMicEnabled;
          this.isVideoOff = !resolved?.isCameraEnabled;
        });

      this.liveKitRoom
        .getRemoteParticipants$()
        .pipe(takeUntil(this.destroy$))
        .subscribe((rp) => {
          this.remoteParticipants = this.withMetas(rp);
          this.updateMinimizedVideo();
        });
    } catch (error) {
      console.error('Failed to init LiveKit room', error);
      this.callEnded.emit();
    }
  }

  refreshPage(): void {
    try {
      window.location.reload();
    } catch {}
  }

  private showPartialPermissionToast(
    sendAudio: boolean,
    sendVideo: boolean,
    isVideoCall: boolean
  ): void {
    const missingAudio = !sendAudio;
    const missingVideo = isVideoCall && !sendVideo;
    if (!missingAudio && !missingVideo) return;

    let key = 'call.permission.partialAudio';
    if (missingAudio && missingVideo) {
      key = 'call.permission.partialAudioVideo';
    } else if (missingVideo) {
      key = 'call.permission.partialVideo';
    }

    if (this.lastPartialToastKey === key) return;
    this.lastPartialToastKey = key;

    this.toastService.show({
      header: this.translate.instant('general.warning'),
      body: this.translate.instant(key),
      classname: 'bg-warning text-white',
      delay: 5000,
    });
  }

  private subscribeToHubEvents(): void {
    this.groupCallHub
      .onGroupCallEnded()
      .pipe(
        takeUntil(this.destroy$),
        filter((ev) => ev.callId === this.callId)
      )
      .subscribe(() => {
        this.leaveCall(true);
      });

    this.groupCallHub
      .onParticipantJoined()
      .pipe(
        takeUntil(this.destroy$),
        filter((ev) => ev.callId === this.callId)
      )
      .subscribe((ev) => {
        const p = ev?.participant;
        if (!p?.userId) return;
        this.setParticipantMeta(
          p.userId,
          p.userName,
          p.professionalTitle,
          p.branchName
        );
        this.localParticipant = this.withMeta(this.localParticipant);
        this.remoteParticipants = this.withMetas(this.remoteParticipants);
      });
  }

  private subscribeToOverlayState(): void {
    this.overlayService
      .getPayload$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        if (payload) {
          const user: any = this.currentUserService.GetSession();
          this.refreshParticipantTitles(payload);
          if (user?.id) {
            const me = payload.participants.find((p) => p.userId === user.id);
            if (me) {
              this.myRole = me.role;
            }
          }
        }
      });

    this.overlayService
      .getMinimized$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((min) => {
        this.isMinimized = min;
        if (min) {
          setTimeout(() => this.updateMinimizedVideo(), 100);
          document.body.classList.add('vc-modal-minimized');
        } else {
          document.body.classList.remove('vc-modal-minimized');
        }
        this.cdr.detectChanges();
      });
  }

  async toggleMute(): Promise<void> {
    if (!this.canSendAudio && this.isMuted) {
      return;
    }
    const nextMuted = !this.isMuted;
    try {
      await this.liveKitRoom.setMicrophoneEnabled(!nextMuted);
      await this.groupCallHub.toggleMedia({
        callId: this.callId,
        isAudioMuted: nextMuted,
      });
      this.isMuted = nextMuted;
    } catch {
      this.isMuted = true;
      if (!nextMuted) {
        this.canSendAudio = false;
      }
    }
  }

  async toggleVideo(): Promise<void> {
    if (!this.canSendVideo && this.isVideoOff) {
      return;
    }
    const nextVideoOff = !this.isVideoOff;
    try {
      await this.liveKitRoom.setCameraEnabled(!nextVideoOff);
      await this.groupCallHub.toggleMedia({
        callId: this.callId,
        isVideoMuted: nextVideoOff,
      });
      this.isVideoOff = nextVideoOff;
    } catch {
      this.isVideoOff = true;
      if (!nextVideoOff) {
        this.canSendVideo = false;
      }
    }
  }

  private refreshParticipantTitles(payload: {
    participants?: {
      userId: string;
      userName?: string;
      branchName?: string;
      professionalTitle?: number | string | null;
    }[];
  }): void {
    this.participantMetaByUserId.clear();
    (payload?.participants || []).forEach((p) => {
      if (!p?.userId) return;
      this.participantMetaByUserId.set(p.userId, {
        name: p.userName,
        branchName: p.branchName,
        professionalTitle: p.professionalTitle ?? null,
      });
    });
    this.localParticipant = this.withMeta(this.localParticipant);
    this.remoteParticipants = this.withMetas(this.remoteParticipants);
  }

  private setParticipantMeta(
    userId: string,
    name?: string,
    professionalTitle?: number | string | null,
    branchName?: string
  ): void {
    if (!userId) return;
    this.participantMetaByUserId.set(userId, {
      name,
      branchName,
      professionalTitle: professionalTitle ?? null,
    });
  }

  private resolveMetaForIdentity(identity: string): {
    name?: string;
    branchName?: string;
    professionalTitle?: number | string | null;
  } | null {
    const direct = this.participantMetaByUserId.get(identity);
    if (direct) return direct;

    // Fallback: identity may include userId (e.g., prefixed/suffixed)
    for (const [userId, meta] of this.participantMetaByUserId.entries()) {
      if (identity.includes(userId)) return meta;
    }
    return null;
  }

  private withMeta(
    participant: ParticipantMedia | null
  ): ParticipantMedia | null {
    if (!participant) return participant;
    const meta = this.resolveMetaForIdentity(participant.identity);
    const sessionUser: any = this.currentUserService.GetSession();
    const localIdentity =
      this.liveKitRoom.getRoom()?.localParticipant?.identity;
    const isLocal =
      sessionUser?.id === participant.identity ||
      (localIdentity && localIdentity === participant.identity);
    const fallbackName = isLocal ? sessionUser?.name : null;
    const fallbackTitle = isLocal ? sessionUser?.professionalTitle : null;
    const fallbackBranch = isLocal
      ? sessionUser?.branchName ?? sessionUser?.BranchName ?? null
      : null;
    return {
      ...participant,
      name: meta?.name || fallbackName || participant.name,
      branchName: meta?.branchName || fallbackBranch || participant.branchName,
      professionalTitle:
        meta?.professionalTitle ??
        fallbackTitle ??
        participant.professionalTitle ??
        null,
    };
  }

  private withMetas(participants: ParticipantMedia[]): ParticipantMedia[] {
    return (participants || []).map(
      (p) => this.withMeta(p) as ParticipantMedia
    );
  }

  async leaveCall(cleanupOnly = false): Promise<void> {
    this.playEndToneOnce();
    if (!cleanupOnly) {
      const isHost = this.myRole === ParticipantRole.Host;
      const isLastParticipant = this.remoteParticipants.length === 0;

      if (isHost || isLastParticipant) {
        try {
          await this.groupCallHub.endGroupCall({ callId: this.callId });
        } catch {
          try {
            await this.groupCallHub.leaveGroupCall({ callId: this.callId });
          } catch {}
        }
      } else {
        try {
          await this.groupCallHub.leaveGroupCall({ callId: this.callId });
        } catch {}
      }
    }

    this.groupCallHub.setActiveCall(null);
    this.overlayService.end();
    this.callEnded.emit();
  }

  async leaveCallAsParticipant(): Promise<void> {
    try {
      await this.groupCallHub.leaveGroupCall({ callId: this.callId });
    } catch {}
    this.groupCallHub.setActiveCall(null);
    this.overlayService.end();
    this.callEnded.emit();
  }

  private async playEndToneOnce(): Promise<void> {
    if (this.endTonePlayed) return;
    this.endTonePlayed = true;

    try {
      await this.audioUnlock.waitUntilUnlocked(2000).catch(() => {});
      const audio = new Audio('/assets/sounds/call-ended.mp3');
      audio.volume = 0.6;
      await audio.play();
    } catch {}
  }

  toggleMinimize(): void {
    if (this.isMinimized) {
      this.overlayService.maximize();
    } else {
      this.overlayService.minimize();
    }
  }

  private updateMinimizedVideo(): void {
    if (!this.isMinimized || !this.minVideo?.nativeElement) return;

    const activeSpeaker =
      this.remoteParticipants.find((p) => p.isSpeaking) ||
      this.remoteParticipants[0];

    if (activeSpeaker) {
      this.liveKitRoom.attachParticipantVideo(
        activeSpeaker.identity,
        this.minVideo.nativeElement
      );
    } else if (this.localParticipant) {
      this.liveKitRoom.attachLocalVideo(this.minVideo.nativeElement);
    }
  }

  // --- Minimize Logic Handlers ---

  private onKeyDown = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') {
      this.toggleMinimize();
    }
  };

  get minimizedStyle(): any {
    return {
      left: this.minimizedLeft + 'px',
      bottom: this.minimizedBottom + 'px',
      width: this.minimizedWidth + 'px',
      height: this.minimizedHeight + 'px',
    };
  }

  onMinMouseDown(evt: MouseEvent) {
    if ((evt.target as HTMLElement).closest('button')) return;

    this.dragActive = true;
    this.dragOffsetX = evt.clientX - this.minimizedLeft;
    this.dragOffsetY =
      evt.clientY -
      (window.innerHeight - this.minimizedBottom - this.minimizedHeight);

    document.addEventListener('mousemove', this.onDocMouseMove);
    document.addEventListener('mouseup', this.onDocMouseUp);
    evt.preventDefault();
  }

  onResizeMouseDown(evt: MouseEvent, edge: string) {
    evt.stopPropagation();
    evt.preventDefault();
    this.resizeActive = true;
    this.resizeEdge = edge;
    this.resizeStartX = evt.clientX;
    this.resizeStartY = evt.clientY;
    this.resizeStartWidth = this.minimizedWidth;
    this.resizeStartHeight = this.minimizedHeight;
    this.resizeStartLeft = this.minimizedLeft;
    this.resizeStartBottom = this.minimizedBottom;

    document.addEventListener('mousemove', this.onDocMouseMove);
    document.addEventListener('mouseup', this.onDocMouseUp);
  }

  private onDocMouseMove = (evt: MouseEvent) => {
    if (this.dragActive) {
      const width = this.minimizedWidth;
      const height = this.minimizedHeight;
      const PADDING = 8;

      const newLeft = Math.max(
        PADDING,
        Math.min(
          window.innerWidth - width - PADDING,
          evt.clientX - this.dragOffsetX
        )
      );
      const newTop = Math.max(
        PADDING,
        Math.min(
          window.innerHeight - height - PADDING,
          evt.clientY - this.dragOffsetY
        )
      );

      this.minimizedLeft = newLeft;
      this.minimizedBottom = window.innerHeight - newTop - height;
      this.cdr.detectChanges();
    } else if (this.resizeActive) {
      const dx = evt.clientX - this.resizeStartX;
      const dy = evt.clientY - this.resizeStartY;

      if (this.resizeEdge.includes('right')) {
        this.minimizedWidth = Math.max(160, this.resizeStartWidth + dx);
      }
      if (this.resizeEdge.includes('left')) {
        const newWidth = Math.max(160, this.resizeStartWidth - dx);
        this.minimizedLeft =
          this.resizeStartLeft + (this.resizeStartWidth - newWidth);
        this.minimizedWidth = newWidth;
      }
      if (this.resizeEdge.includes('bottom')) {
        const newHeight = Math.max(100, this.resizeStartHeight + dy);
        this.minimizedBottom =
          this.resizeStartBottom - (newHeight - this.resizeStartHeight);
        this.minimizedHeight = newHeight;
      }
      if (this.resizeEdge.includes('top')) {
        this.minimizedHeight = Math.max(100, this.resizeStartHeight - dy);
      }
      this.cdr.detectChanges();
    }
  };

  private onDocMouseUp = () => {
    this.dragActive = false;
    this.resizeActive = false;
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('mouseup', this.onDocMouseUp);
  };

  private startTimer(): void {
    this.startTime = Date.now();
    this.timerInterval = setInterval(() => {
      const seconds = Math.floor((Date.now() - this.startTime) / 1000);
      const mm = Math.floor(seconds / 60)
        .toString()
        .padStart(2, '0');
      const ss = (seconds % 60).toString().padStart(2, '0');
      this.duration = `${mm}:${ss}`;
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  get gridClass(): string {
    const total = this.remoteParticipants.length + 1;
    if (total === 1) return 'single';
    if (total === 2) return 'double';
    if (total === 3) return 'triple';
    if (total === 4) return 'quad';
    return 'grid';
  }

  get muteIcon(): string {
    return this.isMuted
      ? '/assets/icons/mic-off.svg'
      : '/assets/icons/mic-on.svg';
  }

  get cameraIcon(): string {
    return this.isVideoOff
      ? '/assets/icons/camera-off.svg'
      : '/assets/icons/camera-on.svg';
  }

  get hangupIcon(): string {
    return '/assets/icons/phone-off.svg';
  }

  get permissionInfoKey(): string | null {
    const sendAudio = this.canSendAudio && !this.isMuted;
    const sendVideo =
      this.callType === CallType.Video && this.canSendVideo && !this.isVideoOff;
    const missingAudio = !sendAudio;
    const missingVideo = this.callType === CallType.Video && !sendVideo;
    if (!missingAudio && !missingVideo) return null;
    if (missingAudio && missingVideo) return 'call.permission.partialAudioVideo';
    if (missingVideo) return 'call.permission.partialVideo';
    return 'call.permission.partialAudio';
  }

  get minimizedParticipant(): ParticipantMedia | null {
    return (
      this.remoteParticipants.find((p) => p.isSpeaking) ||
      this.remoteParticipants[0] ||
      this.localParticipant
    );
  }
}
